import {
	Card,
	Flex,
	CircularProgress,
	CircularProgressLabel,
	Text,
} from "@chakra-ui/react";
import { Icon } from "assets/Icon";
import { useEffect } from "react";
import { formatBytes } from "utils/formatBytes";
import { cn, filenameSpliter, getOriginalFileName } from "utils/helpers";
import { Badge } from "../Badge";
import useChunkFileUpload from "hooks/useChunkFileUpload";
import Tooltip from "components/Tooltip";
import { downloadFileNoPopup } from "helpers/linkHelper";

export const FileCard = ({
	file,
	setFiles,
	isPreview = false,
	uploadProgress,
	categoryTag,
	className,
	fileExtraData,
	onFileUploadComplete,
	handleFileUploadComplete,
	handleFileUploadStart,
	handleFileUploadError,
	isDraggable,
	stripHashFromFilename = false,
}) => {
	const rawFilename =
		file instanceof File
			? file?.path
			: file?.document ||
			file?.name ||
			file?.path ||
			file?.file_name ||
			file?.title ||
			"-";
	const filePath = file.path ?? file.file_path;

	const filename = stripHashFromFilename
		? getOriginalFileName(rawFilename)
		: rawFilename;
	const description = file?.description;
	const filesize =
		file instanceof File
			? formatBytes(file?.size)
			: !file?.file_size
				? null
				: formatBytes(file.file_size);

	const ext = filenameSpliter(filename).ext;

	const { uploadFile, progress, isUploading, error, cancelUpload } =
		useChunkFileUpload({
			onFileUploadComplete,
			handleFileUploadComplete,
		});

	// Calculate the background color based on the upload progress
	const bgColor =
		uploadProgress < 50
			? "red.50"
			: uploadProgress < 75
				? "yellow.50"
				: "green.50";

	useEffect(() => {
		if (file instanceof File && fileExtraData) {
			uploadFile(file, fileExtraData);
			// this function adds loading status to an array so it should be only called when file is uploaded
			if (handleFileUploadStart && file instanceof File) {
				handleFileUploadStart();
			}
		}
		
	}, [file]);

	useEffect(() => {
		if (handleFileUploadError && error) {
			handleFileUploadError();
		}
		
	}, [error]);

	const cancelOrDelete = () => {
		if (progress < 100 && file.uploadId) {
			cancelUpload(file.uploadId);
		}

		setFiles();
	};

	// Only read-only preview rows turn the filename into a download link; upload
	// and drag/cancel surfaces keep it as inert text.
	const isFilenameDownloadable =
		isPreview && Boolean(filePath) && !(file instanceof File);

	const downloadOrDeleteText = isPreview ? (
		"Download file"
	) : (
		<Text>
			Remove file from Submission
			<br />
			(cannot be undone)
		</Text>
	);
	const hyperlinkDownload = (
		<Tooltip
			label={downloadOrDeleteText}
			className={"cursor-pointer"}
		>
			<a
				href={filePath || ""}
				target="_blank"
				rel="noreferrer"
				download={filename ? "download" : null}
				className={cn(
					"flex cursor-pointer rounded-full bg-gray-200 p-2 hover:bg-red-200",
					isPreview && "hover:bg-utility-brand-200",
					!categoryTag && "group-hover:flex"
				)}
			>
				<Icon
					icon={isPreview ? "download" : "trash"}
					style={{
						width: "18px",
						height: "18px",
						cursor: "pointer",
						color: "black",
					}}
				/>
			</a>
		</Tooltip>
	);

	return (
		<Card
			className={cn(
				`border-gray-outline hover:text-purple group my-4 border px-4 py-3 hover:border-utility-brand-100 hover:bg-utility-brand-100 ${bgColor} min-h-[72px] justify-center`,
				className
			)}
			radius="sm"
		>
			<Flex className="relative items-center justify-between gap-4">
				<Flex className="relative items-center justify-between gap-4">
					{!isPreview && isDraggable && (
						<Icon
							icon={"six-dots-vertical"}
							size="md"
						/>
					)}
					<Icon
						icon={ext}
						size="md"
					/>
					<div>
						{isFilenameDownloadable ? (
							<a
								href={filePath}
								target="_blank"
								rel="noreferrer"
								download="download"
								aria-label={`Download ${filename}`}
								className="block cursor-pointer break-all text-sm font-semibold hover:underline"
							>
								{filename}
							</a>
						) : (
							<p className="break-all text-sm font-semibold">{filename}</p>
						)}
						{filesize && <p className="text-sm font-thin">{filesize}</p>}
						{description && <p className="pt-1 text-sm">{description}</p>}
					</div>
				</Flex>
				{categoryTag ? (
					<div className="flex items-center gap-6">
						<Badge
							size="lg"
							className="!text-sm !font-medium"
						>
							{categoryTag}
						</Badge>
						{hyperlinkDownload}
					</div>
				) : (
					<div className="flex items-center">
						{!isPreview && (
							<div className="flex group-hover:hidden">
								<Icon icon="check" />
							</div>
						)}
						{filePath && !(file instanceof File) && isPreview ? (
							hyperlinkDownload
						) : (
							<Tooltip label={downloadOrDeleteText}>
								<button
									className={cn(
										"hidden rounded-full bg-gray-200 p-2 hover:bg-red-200 group-hover:flex",
										isPreview && "hover:bg-utility-brand-200"
									)}
									onClick={() =>
										isPreview
											? downloadFileNoPopup(filePath, file.name, true)
											: cancelOrDelete()
									}
									type="button"
								>
									<Icon
										icon={isPreview ? "download" : "trash"}
										style={{
											width: "18px",
											height: "18px",
											cursor: "pointer",
											color: "black",
										}}
									/>
								</button>
							</Tooltip>
						)}
						{isUploading && progress !== 100 && (
							<CircularProgress
								value={`${error ? 100 : progress}`}
								color={`${error ? "red.500" : "green.400"}`}
							>
								<CircularProgressLabel>
									{error ? (
										<button
											onClick={() => {
												uploadFile(file, fileExtraData);
												handleFileUploadStart();
											}}
											type="button"
										>
											Retry
										</button>
									) : (
										`${progress}%`
									)}
								</CircularProgressLabel>
							</CircularProgress>
						)}
					</div>
				)}
			</Flex>
		</Card>
	);
};
